import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ConstantsService } from '../../fidelidade/ConstantsService';
import { RotaGuardClient } from '../../guards/auth-loja.guard';
import { AutorizacaoService } from '../../services/autorizacao.service';
import { NotificationService } from '../../services/notification.service';
import { ChoiceGroupVM, LibraryGroupVM, ProductCatalogItemVM, ProductEditorVM } from '../produtos-v2.models';
import { ProdutosV2FacadeService } from '../produtos-v2.facade';

type NovoAdicionalStepV2 = 'targets' | 'editor' | 'done';

interface LinkResultV2 {
  product: ProductCatalogItemVM;
  ok: boolean;
  error?: string;
}

/**
 * Cria um adicional uma única vez e o aplica em vários produtos.
 * O grupo nasce no primeiro produto marcado (o editor exige um produto de origem)
 * e, depois de salvo, é vinculado aos demais pela biblioteca (sem clonar).
 */
@Component({
  selector: 'app-tela-novo-adicional-v2',
  templateUrl: './tela-novo-adicional-v2.component.html',
  styleUrls: ['./tela-novo-adicional-v2.component.scss']
})
export class TelaNovoAdicionalV2Component implements OnInit, OnDestroy {
  empresa: any;
  step: NovoAdicionalStepV2 = 'targets';
  loading = true;
  loadingEditor = false;
  linking = false;
  errorMessage = '';
  preSelecionados: Array<number | string> = [];
  escolhidos: ProductCatalogItemVM[] = [];
  editor: ProductEditorVM | null = null;
  savedGroup: ChoiceGroupVM | null = null;
  linkResults: LinkResultV2[] = [];

  private empresaSubscription?: Subscription;
  private templates: any[] = [];
  private linked = false;

  constructor(
    private constantsService: ConstantsService,
    private facade: ProdutosV2FacadeService,
    private route: ActivatedRoute,
    private router: Router,
    private autorizacaoService: AutorizacaoService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.empresaSubscription = this.constantsService.empresa$.subscribe((empresa) => {
      if (!empresa?.catalogo?.id) return;
      this.empresa = empresa;
      void this.load();
    });
  }

  ngOnDestroy(): void {
    this.empresaSubscription?.unsubscribe();
  }

  get canEditStructure(): boolean {
    return !this.empresa?.id || !this.empresa?.catalogo?.compartilhado;
  }

  get canEditPrices(): boolean {
    return this.canEditStructure
      || (!!this.empresa?.catalogo?.precoPorEmpresa
        && RotaGuardClient.alterarPrecoCardapioCompartilhado(this.autorizacaoService.getUser()));
  }

  get canEditAvailability(): boolean {
    return this.canEditStructure || !!this.empresa?.catalogo?.disponibilidadePorEmpresa;
  }

  get canEditOptionDisplay(): boolean {
    return RotaGuardClient.podeEditarExibicaoOpcoes(this.autorizacaoService.getUser(), this.empresa);
  }

  get selectedProducts(): ProductCatalogItemVM[] {
    return this.escolhidos;
  }

  get selectedCount(): number {
    return this.selectedProducts.length;
  }

  get sourceProduct(): ProductCatalogItemVM | null {
    return this.selectedProducts[0] || null;
  }

  get otherProducts(): ProductCatalogItemVM[] {
    return this.selectedProducts.slice(1);
  }

  get linkedOk(): number {
    return this.linkResults.filter((result) => result.ok).length;
  }

  get linkedFailed(): LinkResultV2[] {
    return this.linkResults.filter((result) => !result.ok);
  }

  load(): void {
    const preselected = Number(this.route.snapshot.queryParams['produto'] || 0);
    this.preSelecionados = preselected ? [preselected] : [];
    this.loading = false;
  }

  async continueToEditor(produtos: ProductCatalogItemVM[] = this.escolhidos): Promise<void> {
    this.escolhidos = produtos;
    if (!this.canEditStructure) {
      this.notificationService.showError('A estrutura dos adicionais é controlada pelo catálogo da rede.');
      return;
    }
    const source = this.sourceProduct;
    if (!source?.id) {
      this.notificationService.showError('Marque pelo menos um produto para aplicar o adicional.');
      return;
    }

    this.loadingEditor = true;
    this.errorMessage = '';
    try {
      if (!this.templates.length) {
        this.templates = await this.facade.carregueTemplates(this.empresa.catalogo);
      }
      this.editor = await this.facade.carregueProduto(this.empresa, source.id, this.templates);
      this.step = 'editor';
    } catch (error: any) {
      this.errorMessage = error?.message || 'Não foi possível abrir o editor do adicional.';
    } finally {
      this.loadingEditor = false;
    }
  }

  backToTargets(): void {
    this.preSelecionados = this.escolhidos.map((produto) => produto.id).filter((id) => id != null) as Array<number | string>;
    if (this.step === 'editor' && this.savedGroup) {
      // Grupo já criado: voltar para a seleção mudaria o produto de origem.
      this.step = 'done';
      return;
    }
    this.editor = null;
    this.step = 'targets';
  }

  async onGroupSaved(group: ChoiceGroupVM): Promise<void> {
    this.savedGroup = group;
    if (this.linked) return;
    this.linked = true;
    await this.linkToOtherProducts(group);
    this.step = 'done';
  }

  async retryFailedLinks(): Promise<void> {
    if (!this.savedGroup) return;
    const failed = this.linkedFailed.map((result) => result.product);
    this.linkResults = this.linkResults.filter((result) => result.ok);
    await this.linkToProducts(this.savedGroup, failed);
  }

  onGroupDeleted(): void {
    this.savedGroup = null;
    this.linked = false;
    this.linkResults = [];
    this.goToCatalog();
  }

  openSavedGroup(): void {
    if (!this.savedGroup?.id || !this.sourceProduct?.id) return;
    void this.router.navigate(['/admin/adicionais-v2', this.savedGroup.id], {
      queryParams: { produto: this.sourceProduct.id }
    });
  }

  createAnother(): void {
    this.step = 'targets';
    this.editor = null;
    this.savedGroup = null;
    this.linked = false;
    this.linkResults = [];
    this.escolhidos = [];
    this.preSelecionados = [];
  }

  goToCatalog(): void {
    void this.router.navigate(['/admin/adicionais-v2']);
  }

  trackByProduct(index: number, product: ProductCatalogItemVM): any {
    return product.id || index;
  }

  trackByResult(index: number, result: LinkResultV2): any {
    return result.product.id || index;
  }

  private async linkToOtherProducts(group: ChoiceGroupVM): Promise<void> {
    await this.linkToProducts(group, this.otherProducts);
  }

  private async linkToProducts(group: ChoiceGroupVM, targets: ProductCatalogItemVM[]): Promise<void> {
    const source = this.sourceProduct;
    if (!targets.length || !source?.id || !group.raw?.id) return;

    const libraryGroup = {
      id: group.raw.id,
      name: group.name,
      type: group.type,
      sharedCount: 1,
      usageLabel: '',
      productId: source.id,
      optionsCount: group.options.length,
      filters: [],
      raw: { ...group.raw, produtoOrigem: { id: source.id, nome: source.name } }
    } as LibraryGroupVM;

    this.linking = true;
    try {
      for (const product of targets) {
        try {
          await this.facade.vinculeGrupoDaBiblioteca(this.empresa.catalogo, product.raw || { id: product.id }, libraryGroup, false);
          this.linkResults.push({ product, ok: true });
        } catch (error: any) {
          this.linkResults.push({ product, ok: false, error: error?.message || 'Falha ao vincular.' });
        }
      }
    } finally {
      this.linking = false;
    }

    const failed = this.linkedFailed.length;
    if (failed) {
      this.notificationService.showError(`${failed} ${failed === 1 ? 'produto ficou' : 'produtos ficaram'} sem o vínculo. Você pode tentar de novo.`);
    }
  }

  private normalize(value: any): string {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }
}
